import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'
import Paper from '@/components/Paper'
import { Alert, Button, Typogaphy } from 'vnetwork-ui'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    getSLADashboardOverview,
    DashboardOverview,
} from '@/modules/Service/services/sla'
import { paths } from '@/utils/path'
import SLAIcons from '@/icons/SLAIcons'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

// Stat card component
const StatCard = ({
    label,
    value,
    subValue,
    color = 'default',
    icon,
}: {
    label: string
    value: string | number
    subValue?: string
    color?: 'default' | 'success' | 'warning' | 'error'
    icon?: React.ReactNode
}) => {
    const colorClasses = {
        default: 'bg-slate-50 text-slate-700',
        success: 'bg-emerald-50 text-emerald-700',
        warning: 'bg-amber-50 text-amber-700',
        error: 'bg-red-50 text-red-700',
    }

    return (
        <div
            className={`rounded-lg p-3 ${colorClasses[color]} flex items-center gap-3`}
        >
            {icon && <div className="text-2xl opacity-70">{icon}</div>}
            <div className="flex-1 min-w-0">
                <Typogaphy size="small" className="text-xs opacity-70 truncate">
                    {label}
                </Typogaphy>
                <Typogaphy className="font-semibold text-lg">{value}</Typogaphy>
                {subValue && (
                    <Typogaphy size="small" className="text-xs opacity-60">
                        {subValue}
                    </Typogaphy>
                )}
            </div>
        </div>
    )
}

// Format minutes to readable duration
const formatMinutes = (minutes: number): string => {
    if (minutes < 60) return `${minutes}m`
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    if (hours < 24) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
    const days = Math.floor(hours / 24)
    const remainingHours = hours % 24
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`
}

const SLADashboardWidget = () => {
    const { t } = useTranslation()

    // Default to current month
    const [dateRange] = useState(() => ({
        start: dayjs().startOf('month').format('YYYY-MM-DD'),
        end: dayjs().endOf('month').format('YYYY-MM-DD'),
    }))

    const { data, isLoading, error } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SURVEY_ANALYTICS_DASHBOARD, dateRange],
        queryFn: () =>
            getSLADashboardOverview({
                start_date: dateRange.start,
                end_date: dateRange.end,
            }),
    })

    const overview: DashboardOverview | undefined = data?.result

    // Calculate compliance percentage
    const compliancePercent = useMemo(() => {
        if (!overview || overview.total_services_with_sla === 0) return 0
        return Math.round(
            (overview.services_meeting_sla / overview.total_services_with_sla) *
                100
        )
    }, [overview])

    if (isLoading) {
        return (
            <Paper className="h-full p-4">
                <div className="animate-pulse space-y-4">
                    <div className="h-6 bg-slate-200 rounded w-1/3"></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="h-20 bg-slate-100 rounded"></div>
                        <div className="h-20 bg-slate-100 rounded"></div>
                        <div className="h-20 bg-slate-100 rounded"></div>
                        <div className="h-20 bg-slate-100 rounded"></div>
                    </div>
                </div>
            </Paper>
        )
    }

    if (error) {
        return (
            <Paper className="h-full p-4">
                <Alert variant="error">
                    {t('sla-dashboard-error', 'Không thể tải dữ liệu SLA')}
                </Alert>
            </Paper>
        )
    }

    if (!overview || overview.total_services_with_sla === 0) {
        return (
            <Paper className="h-full p-4">
                <div className="flex items-center gap-2 mb-3">
                    <SLAIcons className="w-5 h-5 text-primary" />
                    <Typogaphy className="font-semibold">
                        {t('sla-overview', 'SLA Overview')}
                    </Typogaphy>
                </div>
                <div className="text-center py-6 text-slate-500">
                    <Typogaphy>
                        {t(
                            'no-active-sla',
                            'Chưa có dịch vụ nào được cấu hình SLA'
                        )}
                    </Typogaphy>
                </div>
            </Paper>
        )
    }

    return (
        <Paper className="h-full p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <SLAIcons className="w-5 h-5 text-primary" />
                    <Typogaphy className="font-semibold">
                        {t('sla-overview', 'SLA Overview')}
                    </Typogaphy>
                </div>
                <Typogaphy size="small" className="text-slate-500">
                    {dayjs(dateRange.start).format('DD/MM')} -{' '}
                    {dayjs(dateRange.end).format('DD/MM/YYYY')}
                </Typogaphy>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-3 mb-4">
                <StatCard
                    label={t('total-services', 'Dịch vụ có SLA')}
                    value={overview.total_services_with_sla}
                    color="default"
                />
                <StatCard
                    label={t('avg-uptime', 'Uptime TB')}
                    value={`${overview.avg_uptime_percent.toFixed(2)}%`}
                    color={
                        overview.avg_uptime_percent >= 99.5
                            ? 'success'
                            : 'warning'
                    }
                />
                <StatCard
                    label={t('meeting-sla', 'Đạt SLA')}
                    value={overview.services_meeting_sla}
                    subValue={`${compliancePercent}%`}
                    color="success"
                />
                <StatCard
                    label={t('breaching-sla', 'Vi phạm SLA')}
                    value={overview.services_breaching_sla}
                    color={
                        overview.services_breaching_sla > 0
                            ? 'error'
                            : 'success'
                    }
                />
            </div>

            {/* Downtime Summary */}
            <div className="bg-slate-50 rounded-lg p-3 mb-4">
                <Typogaphy size="small" className="text-slate-600 mb-2">
                    {t('downtime-summary', 'Thống kê Downtime')}
                </Typogaphy>
                <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div>
                        <div className="font-semibold text-red-600">
                            {formatMinutes(overview.total_provider_downtime)}
                        </div>
                        <div className="text-slate-500">Provider</div>
                    </div>
                    <div>
                        <div className="font-semibold text-orange-600">
                            {formatMinutes(overview.total_third_party_downtime)}
                        </div>
                        <div className="text-slate-500">Third-party</div>
                    </div>
                    <div>
                        <div className="font-semibold text-blue-600">
                            {formatMinutes(overview.total_scheduled_downtime)}
                        </div>
                        <div className="text-slate-500">Scheduled</div>
                    </div>
                </div>
            </div>

            {/* Incidents count */}
            {overview.total_incidents > 0 && (
                <div className="flex items-center justify-between text-sm mb-3">
                    <span className="text-slate-600">
                        {t('total-incidents', 'Tổng sự cố')}:
                    </span>
                    <span className="font-medium text-red-600">
                        {overview.total_incidents}
                    </span>
                </div>
            )}

            {/* View Detail Link */}
            <Link to={paths.serviceSLADashboard}>
                <Button variant="outlined" size="small" className="w-full">
                    {t('view-detail', 'Xem chi tiết')}
                </Button>
            </Link>
        </Paper>
    )
}

export default SLADashboardWidget
